/**
 * Meeting Data Localization Utilities
 * 
 * Provides translation and normalization for meeting enum values.
 * Handles both new normalized English keys and legacy Portuguese values.
 */

// Legacy Portuguese to normalized English key mappings
const LEGACY_CATEGORY_MAP: Record<string, string> = {
  'Grupo de Apoio': 'supportGroup',
  'Workshop': 'workshop',
  'Meditação': 'meditation',
  'Terapia': 'therapy',
  'Partilha': 'sharing',
  // Elo Meeting Groups taxonomy — see documentation/10. Elo/API_ELO_MEETING_GROUPS.md
  'reuniao_novos': 'reuniaoNovos',
  'reuniao_partilhas': 'reuniaoPartilhas',
  'reuniao_tematica': 'reuniaoTematica',
  'reuniao_estudo_literatura': 'reuniaoEstudoLiteratura',
  'reuniao_ateus_agnosticos': 'reuniaoAteusAgnosticos',
  'reuniao_lgbtqiapn': 'reuniaoLgbtqiapn',
  'reuniao_codependentes': 'reuniaoCodependentes',
  'reuniao_passos': 'reuniaoPassos',
  'reuniao_tradicoes': 'reuniaoTradicoes',
};

const LEGACY_WEEKDAY_MAP: Record<string, string> = {
  'segunda-feira': 'monday',
  'terça-feira': 'tuesday',
  'quarta-feira': 'wednesday',
  'quinta-feira': 'thursday',
  'sexta-feira': 'friday',
  'sábado': 'saturday',
  'domingo': 'sunday',
};

// Create case-insensitive lookup for legacy weekdays
const LEGACY_WEEKDAY_MAP_LOWERCASE = Object.fromEntries(
  Object.entries(LEGACY_WEEKDAY_MAP).map(([key, value]) => [key.toLowerCase(), value])
);

const LEGACY_PERIOD_MAP: Record<string, string> = {
  'Manhã': 'morning',
  'Tarde': 'afternoon',
  'Noite': 'evening',
};

const LEGACY_PLATFORM_MAP: Record<string, string> = {
  'Zoom': 'zoom',
  'Google Meet': 'google-meet',
  'Microsoft Teams': 'microsoft-teams',
  'Jitsi Meet': 'jitsi-meet',
  'Discord': 'discord',
  'Presencial': 'in-person',
};

/**
 * Normalize category value (legacy or new) to English key
 */
export function normalizeCategoryValue(value: string): string {
  return LEGACY_CATEGORY_MAP[value] || value;
}

/**
 * Normalize weekday value (legacy or new) to English key
 * Handles both "Segunda-feira" (capitalized) and "segunda-feira" (lowercase)
 */
export function normalizeWeekdayValue(value: string): string {
  const trimmed = value.trim();
  // Try exact match first (for backwards compatibility)
  if (LEGACY_WEEKDAY_MAP[trimmed]) {
    return LEGACY_WEEKDAY_MAP[trimmed];
  }
  // Try case-insensitive match for legacy Portuguese values
  const lowercase = trimmed.toLowerCase();
  if (LEGACY_WEEKDAY_MAP_LOWERCASE[lowercase]) {
    return LEGACY_WEEKDAY_MAP_LOWERCASE[lowercase];
  }
  // Return as-is if already normalized (english keys like 'monday')
  return trimmed.toLowerCase();
}

/**
 * Normalize period value (legacy or new) to English key
 */
export function normalizePeriodValue(value: string): string {
  return LEGACY_PERIOD_MAP[value] || value;
}

/**
 * Normalize platform value (legacy or new) to English key
 */
export function normalizePlatformValue(value: string): string {
  return LEGACY_PLATFORM_MAP[value] || value;
}

/**
 * Translate category value to localized string
 */
export function translateCategory(value: string, t: (key: string) => string): string {
  const normalized = normalizeCategoryValue(value);
  
  const keyMap: Record<string, string> = {
    'supportGroup': 'common.categories.supportGroup',
    'workshop': 'common.categories.workshop',
    'meditation': 'common.categories.meditation',
    'therapy': 'common.categories.therapy',
    'sharing': 'common.categories.sharing',
    // Elo Meeting Groups taxonomy
    'reuniaoNovos': 'common.categories.reuniaoNovos',
    'reuniaoPartilhas': 'common.categories.reuniaoPartilhas',
    'reuniaoTematica': 'common.categories.reuniaoTematica',
    'reuniaoEstudoLiteratura': 'common.categories.reuniaoEstudoLiteratura',
    'reuniaoAteusAgnosticos': 'common.categories.reuniaoAteusAgnosticos',
    'reuniaoLgbtqiapn': 'common.categories.reuniaoLgbtqiapn',
    'reuniaoCodependentes': 'common.categories.reuniaoCodependentes',
    'reuniaoPassos': 'common.categories.reuniaoPassos',
    'reuniaoTradicoes': 'common.categories.reuniaoTradicoes',
  };
  
  const translationKey = keyMap[normalized];
  return translationKey ? t(translationKey) : value;
}

/**
 * Translate weekday value to localized string
 */
export function translateWeekday(value: string, t: (key: string) => string, short: boolean = false): string {
  const normalized = normalizeWeekdayValue(value);
  
  const prefix = short ? 'common.weekdays.short' : 'common.weekdays.long';
  const keyMap: Record<string, string> = {
    'monday': `${prefix}.monday`,
    'tuesday': `${prefix}.tuesday`,
    'wednesday': `${prefix}.wednesday`,
    'thursday': `${prefix}.thursday`,
    'friday': `${prefix}.friday`,
    'saturday': `${prefix}.saturday`,
    'sunday': `${prefix}.sunday`,
  };
  
  const translationKey = keyMap[normalized];
  return translationKey ? t(translationKey) : value;
}

/**
 * Parse weekday string into array, handling both ';' and ',' delimiters
 * Handles formats:
 * - Old: "Segunda-feira; Terça-feira; Quarta-feira" (Portuguese, semicolon+space)
 * - New: "monday, wednesday, friday" (English, comma+space)
 */
export function parseWeekdays(values: string | string[] | null | undefined): string[] {
  if (Array.isArray(values)) {
    return values.filter(v => typeof v === 'string' && v.length > 0);
  }
  // Defensive: meetings without sessions (or Elo rows missing the flat
  // weekday field after the Phase-1 schema split) may pass null/undefined.
  // Crashing here breaks every Meeting card that uses getMeetingDisplayTime.
  if (typeof values !== 'string' || values.length === 0) {
    return [];
  }

  // Determine delimiter (prefer semicolon if present, otherwise use comma)
  const delimiter = values.includes(';') ? ';' : ',';

  return values
    .split(delimiter)
    .map(day => day.trim())
    .filter(day => day.length > 0);
}

/**
 * Translate array of weekdays to localized comma-separated string
 */
export function translateWeekdays(
  values: string | string[], 
  t: (key: string) => string, 
  short: boolean = false
): string {
  const weekdayArray = parseWeekdays(values);
  return weekdayArray
    .map(day => {
      const label = translateWeekday(day, t, short);
      return label ? label.charAt(0).toUpperCase() + label.slice(1) : label;
    })
    .join(', ');
}

/**
 * Translate period value to localized string
 */
export function translatePeriod(value: string, t: (key: string) => string): string {
  const normalized = normalizePeriodValue(value);
  
  const keyMap: Record<string, string> = {
    'morning': 'common.periods.morning',
    'afternoon': 'common.periods.afternoon',
    'evening': 'common.periods.evening',
  };
  
  const translationKey = keyMap[normalized];
  return translationKey ? t(translationKey) : value;
}

/**
 * Translate platform value to localized string
 */
export function translatePlatform(value: string, t: (key: string) => string): string {
  const normalized = normalizePlatformValue(value);
  
  // Platform names are mostly brand names (Zoom, Discord, etc.)
  // Only 'in-person' needs translation
  if (normalized === 'in-person') {
    return t('meetings.form.fields.platform.inPerson');
  }
  
  // Return capitalized brand names
  const brandNames: Record<string, string> = {
    'zoom': 'Zoom',
    'google-meet': 'Google Meet',
    'microsoft-teams': 'Microsoft Teams',
    'jitsi-meet': 'Jitsi Meet',
    'discord': 'Discord',
  };
  
  return brandNames[normalized] || value;
}

/**
 * Coerce meeting data from legacy format to normalized format
 * Use this when loading meeting data to ensure consistency
 */
export function coerceMeetingData(meetingData: any): any {
  return {
    ...meetingData,
    meeting_category: normalizeCategoryValue(meetingData.meeting_category || ''),
    meeting_period: normalizePeriodValue(meetingData.meeting_period || ''),
    meeting_place: normalizePlatformValue(meetingData.meeting_place || ''),
    meeting_weekday: Array.isArray(meetingData.meeting_weekday)
      ? meetingData.meeting_weekday.map(normalizeWeekdayValue)
      : (meetingData.meeting_weekday || '').split(', ').map((d: string) => normalizeWeekdayValue(d.trim())),
  };
}
